import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Card,
    CardHeader,
    mergeClasses,
    Text,
    Tooltip,
} from '@fluentui/react-components';
import { useFeatureTileStyles } from './FeatureTile.styles';
import { FeatureServiceKey } from '../../../store/features/healthDashboard/healthDashboardSlice';
import { useMemo } from 'react';
import { useSelector } from '../../../store/hooks';
import { useTranslation } from 'react-i18next';
import {
    CheckmarkCircleRegular,
    CloudErrorRegular,
    ErrorCircleRegular,
    QuestionCircleRegular,
    WarningRegular,
} from '@fluentui/react-icons';
import { ServiceHealthStatus } from '../../../model/HealthInfo';

export type FeatureHealthStatus = 'Ok' | 'Warning' | 'Error' | 'Unknown';

export interface FeatureItem {
    id: string;
    label: string;
    status: FeatureHealthStatus;
}

export interface FeatureTileProps {
    title: string;
    icon: React.ReactElement;
    status: FeatureHealthStatus;
    items?: readonly FeatureItem[];
    relatedServices?: readonly FeatureServiceKey[];
    className?: string;
}

export type FeatureTileComponent<P = {}> = React.FC<P & { className?: string }>;

const mapServiceStatus = (status: ServiceHealthStatus): FeatureHealthStatus => {
    switch (status) {
        case 'Unknown':
            return 'Unknown';
        case 'Healthy':
            return 'Ok';
        case 'Unhealthy':
            return 'Error';
    }
};

const FeatureTile: React.FC<FeatureTileProps> = ({
    title,
    icon,
    status = 'Unknown',
    items,
    relatedServices,
    className,
}) => {
    const styles = useFeatureTileStyles();

    const { services } = useSelector((x) => x.healthDashboard);

    const { t } = useTranslation();

    const relatedServicesArray = useMemo(() => {
        if (!services || !relatedServices) {
            return undefined;
        }

        return relatedServices.map((x) => services[x]);
    }, [services, relatedServices]);

    const statusDisplay = useMemo<{
        labelElement: React.ReactElement;
        icon: React.ReactElement;
    }>(() => {
        const labelText = t(`FeatureHealthStatus_${status}`, '');
        let icon: React.ReactElement;

        switch (status) {
            case 'Unknown':
                icon = <QuestionCircleRegular />;
                break;
            case 'Ok':
                icon = <CheckmarkCircleRegular />;
                break;
            case 'Warning':
                icon = <WarningRegular />;
                break;
            case 'Error':
                icon = <ErrorCircleRegular />;
                break;
        }

        let worstHealthStatus: ServiceHealthStatus = 'Healthy';

        for (const srv of relatedServicesArray ?? []) {
            if (srv.status === 'Unhealthy') {
                worstHealthStatus = 'Unhealthy';
                break;
            }

            if (srv.status === 'Unknown') {
                worstHealthStatus = 'Unknown';
            }
        }

        const labelElement =
            worstHealthStatus !== 'Healthy' ? (
                <span className={styles.featureStatusLabel}>
                    {labelText}
                    <Tooltip
                        content={t('Health_UnavailableServices')}
                        relationship="description"
                    >
                        <CloudErrorRegular className={worstHealthStatus} />
                    </Tooltip>
                </span>
            ) : (
                <span className={styles.featureStatusLabel}>{labelText}</span>
            );

        return {
            labelElement,
            icon,
        };
    }, [status, relatedServicesArray, t, styles]);

    return (
        <Card
            appearance="outline"
            className={mergeClasses(styles.root, className)}
        >
            <CardHeader
                image={<div className={styles.featureIcon}>{icon}</div>}
                header={
                    <div className={styles.description}>
                        <Text weight="bold" size={400}>
                            {title}
                        </Text>
                        <div className={styles.featureItems}>
                            {items?.map((x) => {
                                return (
                                    <div
                                        key={x.id}
                                        className={styles.statusItem}
                                    >
                                        <span
                                            className={mergeClasses(
                                                styles.statusIndicator,
                                                x.status
                                            )}
                                        />
                                        <span>{x.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                }
            />
            <Accordion collapsible>
                <AccordionItem
                    value={'item'}
                    disabled={!relatedServicesArray?.length}
                >
                    <AccordionHeader
                        expandIconPosition="end"
                        icon={statusDisplay.icon}
                        className={mergeClasses(styles.featureStatus, status)}
                        expandIcon={
                            !relatedServicesArray?.length ? <></> : undefined
                        }
                    >
                        {statusDisplay.labelElement}
                    </AccordionHeader>
                    <AccordionPanel className={styles.services}>
                        {relatedServicesArray?.map((x) => {
                            const statusStr = t(
                                `ServiceHealthStatus_${x.status}`,
                                ''
                            );

                            const label = statusStr
                                ? `${x.label} (${statusStr})`
                                : x.label;

                            return (
                                <div key={x.id} className={styles.statusItem}>
                                    <span
                                        className={mergeClasses(
                                            styles.statusIndicator,
                                            mapServiceStatus(x.status)
                                        )}
                                    />
                                    <span>{label}</span>
                                </div>
                            );
                        })}
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        </Card>
    );
};

export default FeatureTile;
